import type {
  BlockDefinition,
  ContentEntry,
  JsonObject,
  MediaAsset,
  Page,
  PageRevision,
  PluginDefinition,
  Site,
  SiteDomain,
  SitePlugin,
  Theme,
} from './types';

interface ApiEnvelope<T> {
  data: T;
}

interface ApiErrorEnvelope {
  error?: {message?: string; details?: JsonObject};
}

const baseUrl = (import.meta.env.VITE_IMPAKT_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';
export const authMode = (import.meta.env.VITE_IMPAKT_AUTH_MODE as string | undefined) === 'token' ? 'token' : 'session';
export const loginUrl = (import.meta.env.VITE_IMPAKT_LOGIN_URL as string | undefined) ?? '/login';

export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('impakt_access_token');
  const response = await fetch(`${baseUrl}${path}`, {
    ...options,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(options.body && !(options.body instanceof FormData) ? {'Content-Type': 'application/json'} : {}),
      ...(authMode === 'token' && token ? {Authorization: `Bearer ${token}`} : {}),
      ...options.headers,
    },
  });
  const payload = (await response.json().catch(() => ({}))) as ApiEnvelope<T> & ApiErrorEnvelope;
  if (!response.ok) {
    throw new ApiError(response.status, payload.error?.message ?? `Request failed with status ${response.status}.`);
  }

  return payload.data;
}

export const api = {
  logout: async () => {
    await fetch(`${baseUrl}/auth/logout`, {method: 'POST', credentials: 'same-origin'});
  },
  blocks: () => request<BlockDefinition[]>('/api/v1/site-builder/blocks'),
  sites: () => request<Site[]>('/api/v1/site-builder/sites'),
  site: (id: string) => request<Site>(`/api/v1/site-builder/sites/${id}`),
  createSite: (input: {key: string; name: string; default_locale: string}) => request<Site>(
    '/api/v1/site-builder/sites',
    {method: 'POST', body: JSON.stringify(input)},
  ),
  updateSite: (site: Site, theme: Theme) => request<Site>(
    `/api/v1/site-builder/sites/${site.id}`,
    {method: 'PATCH', body: JSON.stringify({name: site.name, theme, version: site.version})},
  ),
  pages: (siteId: string) => request<Page[]>(`/api/v1/site-builder/sites/${siteId}/pages`),
  createHome: (site: Site) => request<Page>(
    `/api/v1/site-builder/sites/${site.id}/pages`,
    {
      method: 'POST',
      body: JSON.stringify({
        slug: 'home', locale: site.default_locale, title: 'Acasă', layout: [], seo: {}, site_version: site.version,
      }),
    },
  ),
  createPage: (site: Site, input: {slug: string; locale: string; title: string}) => request<Page>(
    `/api/v1/site-builder/sites/${site.id}/pages`,
    {
      method: 'POST',
      body: JSON.stringify({...input, layout: [], seo: {}, site_version: site.version}),
    },
  ),
  updatePage: (site: Site, page: Page) => request<Page>(
    `/api/v1/site-builder/pages/${page.id}`,
    {
      method: 'PATCH',
      body: JSON.stringify({
        title: page.title,
        layout: page.layout,
        seo: page.seo,
        version: page.version,
        site_version: site.version,
      }),
    },
  ),
  revisions: (pageId: string) => request<PageRevision[]>(`/api/v1/site-builder/pages/${pageId}/revisions`),
  restoreRevision: (site: Site, page: Page, version: number) => request<Page>(
    `/api/v1/site-builder/pages/${page.id}/revisions/${version}/restore`,
    {
      method: 'POST',
      body: JSON.stringify({page_version: page.version, site_version: site.version}),
    },
  ),
  publish: (site: Site) => request<Site>(
    `/api/v1/site-builder/sites/${site.id}/publish`,
    {method: 'POST', body: JSON.stringify({version: site.version})},
  ),
  pluginCatalog: () => request<PluginDefinition[]>('/api/v1/site-builder/plugins'),
  plugins: (siteId: string) => request<SitePlugin[]>(`/api/v1/site-builder/sites/${siteId}/plugins`),
  configurePlugin: (site: Site, pluginId: string, enabled: boolean, configuration: JsonObject) => request<SitePlugin>(
    `/api/v1/site-builder/sites/${site.id}/plugins/${pluginId}`,
    {method: 'PATCH', body: JSON.stringify({enabled, configuration, site_version: site.version})},
  ),
  domains: (siteId: string) => request<SiteDomain[]>(`/api/v1/site-builder/sites/${siteId}/domains`),
  addDomain: (site: Site, hostname: string) => request<SiteDomain>(
    `/api/v1/site-builder/sites/${site.id}/domains`,
    {method: 'POST', body: JSON.stringify({hostname, site_version: site.version})},
  ),
  verifyDomain: (site: Site, domainId: string) => request<SiteDomain>(
    `/api/v1/site-builder/domains/${domainId}/verify`,
    {method: 'POST', body: JSON.stringify({site_version: site.version})},
  ),
  makePrimaryDomain: (site: Site, domainId: string) => request<SiteDomain>(
    `/api/v1/site-builder/domains/${domainId}/primary`,
    {method: 'POST', body: JSON.stringify({site_version: site.version})},
  ),
  media: (siteId: string) => request<MediaAsset[]>(`/api/v1/site-builder/sites/${siteId}/media`),
  uploadMedia: (siteId: string, file: File, altText: string) => {
    const body = new FormData();
    body.append('file', file);
    body.append('alt_text', altText);
    return request<MediaAsset>(`/api/v1/site-builder/sites/${siteId}/media`, {method: 'POST', body});
  },
  updateMedia: (mediaId: string, altText: string) => request<MediaAsset>(
    `/api/v1/site-builder/media/${mediaId}`,
    {method: 'PATCH', body: JSON.stringify({alt_text: altText})},
  ),
  content: async (type = 'article') => {
    const items: ContentEntry[] = [];
    for (let page = 1; page <= 100; page += 1) {
      const batch = await request<ContentEntry[]>(`/api/v1/cms/content?type=${encodeURIComponent(type)}&per_page=100&page=${page}`);
      items.push(...batch);
      if (batch.length < 100) break;
    }
    return items;
  },
  createContent: (input: {type: string; slug: string; locale: string; title: string; content: JsonObject}) => request<ContentEntry>(
    '/api/v1/cms/content',
    {method: 'POST', body: JSON.stringify(input)},
  ),
  updateContent: (entry: ContentEntry) => request<ContentEntry>(
    `/api/v1/cms/content/${entry.id}`,
    {method: 'PATCH', body: JSON.stringify({title: entry.title, content: entry.content, version: entry.version})},
  ),
  publishContent: (entry: ContentEntry) => request<ContentEntry>(
    `/api/v1/cms/content/${entry.id}/publish`,
    {method: 'POST', body: JSON.stringify({version: entry.version})},
  ),
  unpublishContent: (entry: ContentEntry) => request<ContentEntry>(
    `/api/v1/cms/content/${entry.id}/unpublish`,
    {method: 'POST', body: JSON.stringify({version: entry.version})},
  ),
  archiveContent: (entry: ContentEntry) => request<ContentEntry>(
    `/api/v1/cms/content/${entry.id}/archive`,
    {method: 'POST', body: JSON.stringify({version: entry.version})},
  ),
};
